import { resolveMediaUrl } from "./resolveMediaUrl";

/** Matches common video file extensions in paths or URLs (query/hash allowed). */
export const VIDEO_URL_REGEX =
  /\.(webm|mp4|mov|ogg|m4v|avi|mkv)(\?|#|$)/i;

/**
 * @param {unknown} url
 * @returns {boolean}
 */
export function isVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  return VIDEO_URL_REGEX.test(url.trim());
}

/**
 * Best-effort MIME for a `<source type>` from the URL path.
 * @param {string} url
 * @returns {string}
 */
export function videoMimeFromUrl(url) {
  const lower = url.toLowerCase();
  if (lower.includes(".webm")) return "video/webm";
  if (lower.includes(".mp4")) return "video/mp4";
  if (lower.includes(".mov")) return "video/quicktime";
  if (lower.includes(".ogg")) return "video/ogg";
  return "video/mp4";
}

/**
 * Collect string paths from CMS-shaped blocks: `media` as string | string[],
 * or `images` when `media` yields nothing.
 *
 * @param {unknown} raw
 * @returns {string[]}
 */
export function collectMediaPathsFromContent(raw) {
  if (!raw || typeof raw !== "object") return [];
  const out = [];
  const push = (v) => {
    if (typeof v === "string" && v.trim()) out.push(v.trim());
  };
  const obj = /** @type {{ media?: string | string[], images?: string[] }} */ (
    raw
  );
  if (Array.isArray(obj.media)) {
    obj.media.forEach(push);
  } else if (typeof obj.media === "string" && /\r?\n/.test(obj.media)) {
    obj.media.split(/\r?\n/).forEach(push);
  } else {
    push(obj.media);
  }
  if (out.length === 0 && Array.isArray(obj.images)) {
    obj.images.forEach(push);
  }
  // Legacy CMS key "image" (e.g. weLaunch before Key was renamed to "media")
  if (out.length === 0) {
    const legacy = /** @type {{ image?: string | string[] }} */ (obj).image;
    if (Array.isArray(legacy)) legacy.forEach(push);
    else push(legacy);
  }
  return out;
}

/**
 * Same as {@link collectMediaPathsFromContent}, then maps through {@link resolveMediaUrl} and drops empties.
 *
 * @param {unknown} raw
 * @returns {string[]}
 */
export function getResolvedContentMediaUrls(raw) {
  return collectMediaPathsFromContent(raw)
    .map((p) => resolveMediaUrl(p))
    .filter(Boolean);
}
